import { RevisionChangeSummary } from "@faro-lotv/data-management";
import { FaroText } from "@faro-lotv/flat-ui";
import { Box } from "@mui/system";
import { type JSX } from "react";

type RevisionChangesListProps = {
  /** A summary of the changes performed in the current revision. */
  revisionChanges: RevisionChangeSummary;
};

type RevisionChangeGroup = {
  title: string;
  changes: RevisionChangeListItem[];
};

type RevisionChangeListItem = {
  key: string;
  count: number;
  label: string;
};

/** @returns Revision changes grouped by change type. */
export function RevisionChangesList({
  revisionChanges,
}: RevisionChangesListProps): JSX.Element {
  const changeGroups: RevisionChangeGroup[] = [
    {
      title: "Added",
      changes: [
        {
          key: "clusters-added",
          count: revisionChanges.clustersAdded,
          label: "cluster",
        },
        {
          key: "captures-added",
          count: revisionChanges.capturesAdded,
          label: "capture",
        },
      ],
    },
    {
      title: "Deleted",
      changes: [
        {
          key: "clusters-deleted",
          count: revisionChanges.clustersDeleted,
          label: "cluster",
        },
        {
          key: "captures-deleted",
          count: revisionChanges.capturesDeleted,
          label: "capture",
        },
      ],
    },
    {
      title: "Modified",
      changes: [
        {
          key: "clusters-modified",
          count: revisionChanges.clustersModified,
          label: "cluster",
        },
        {
          key: "captures-modified",
          count: revisionChanges.capturesModified,
          label: "capture",
        },
      ],
    },
  ];
  const groupsWithChanges = changeGroups
    .map((group) => ({
      ...group,
      changes: group.changes.filter((change) => change.count > 0),
    }))
    .filter((group) => group.changes.length > 0);

  if (groupsWithChanges.length === 0 && revisionChanges.sum > 0) {
    return (
      <Box component="ul" sx={{ mt: 0, pl: 0, listStylePosition: "inside" }}>
        <Box component="li">Internal registration data was updated.</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0 }}>
      {groupsWithChanges.map((group) => (
        <Box key={group.title} sx={{ mb: 1 }}>
          <FaroText variant="heading14">{group.title}</FaroText>
          <Box
            component="ul"
            sx={{ mt: 0, mb: 0, pl: 0, listStylePosition: "inside" }}
          >
            {group.changes.map((change) => (
              <Box component="li" key={change.key}>
                <FaroText variant="bodyM">
                  {change.count} {pluralizeRevisionChangeLabel(change)}
                </FaroText>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function pluralizeRevisionChangeLabel({
  count,
  label,
}: RevisionChangeListItem): string {
  return count === 1 ? label : `${label}s`;
}
